import { projectsStardogRequestDispatchers } from 'src/actions/projects/actionCreators';
import {
  ProjectModelClass,
  ProjectModelProperty,
} from 'src/store/state/projects';
import { parseModelClassResults } from 'src/utils/binding/parseModelClassResults';
import { parseModelPropertyResults } from 'src/utils/binding/parseModelPropertyResults';

/**
 * Fetches and parses model classes and properties from the database.
 * Returns the processed classes and properties without dispatching redux actions
 * when shouldNotDispatch is true.
 */
export const fetchModelClassesAndProperties = async ({
  projectId,
  modelName,
  databaseId,
  namedGraphs,
  shouldNotDispatch,
}: {
  projectId: string;
  modelName: string;
  databaseId: string;
  namedGraphs: string[];
  shouldNotDispatch?: boolean;
}): Promise<{
  classes: Record<string, ProjectModelClass> | null;
  properties: Record<string, ProjectModelProperty> | null;
}> => {
  const [classesResponse, propertiesResponse] = await Promise.all([
    projectsStardogRequestDispatchers.requestProjectModelClasses({
      id: projectId,
      modelName,
      databaseId,
      namedGraphs,
      shouldNotDispatch,
    }),
    projectsStardogRequestDispatchers.requestProjectModelProperties({
      id: projectId,
      modelName,
      databaseId,
      namedGraphs,
      shouldNotDispatch,
    }),
  ]);

  const classes = classesResponse.payload?.response?.body?.results
    ? parseModelClassResults(
        classesResponse.payload.response.body.results.bindings
      )
    : null;

  const importedProperties = propertiesResponse.payload?.response?.body?.results
    ?.bindings
    ? parseModelPropertyResults(
        propertiesResponse.payload.response.body.results.bindings
      )
    : null;

  const properties = importedProperties
    ? Object.values(importedProperties).reduce<
        Record<string, ProjectModelProperty>
      >((acc, property) => {
        if (classes && classes[property.iri]) {
          return acc;
        }

        acc[property.iri] = {
          ...property,
          isDatatype: !property.isRelationship && Boolean(property.isAttribute),
        };

        return acc;
      }, {})
    : null;

  return { classes, properties };
};
